import { isNumber } from '@xemplo/common-util';
import { Country } from '@xemplo/country-helper';

import { NumberFormat, NumberFormatOptions } from '../number-field.types';

type Result = {
  raw: number | undefined;
  formatted: string;
};

type Primitives = string | number | bigint | undefined | null;

/**
 * A utility class for formatting and unformatting numbers based on locale and number format options.
 */
export class NumberFormatter {
  private _locale: Country.Locale | string;
  private _numberFormat: NumberFormat;
  private _unit?: string;

  /**
   * Constructs a new NumberFormatter.
   * @param props - Formatting options including locale, currency style, etc.
   */
  constructor(private props: NumberFormatOptions) {
    this._locale = props.locale || navigator.language;
    this._numberFormat = props.numberFormat || NumberFormat.Decimal;
    this._unit = props.unit?.trim();
  }

  /**
   * Formats a primitive value (string, number, bigint, etc.) into a locale-aware string.
   * @param value - The value to be formatted.
   * @returns An object containing the raw numeric value and the formatted string.
   */
  public format(value: Primitives): Result {
    if (value == null) return { raw: undefined, formatted: '' };

    const formatter = this.getFormatter();
    if (isNumber(value)) return formatter(value);

    const raw = this.unformat(value.toString());
    if (raw === undefined || isNaN(raw)) {
      return { raw: undefined, formatted: '' };
    }

    return formatter(raw);
  }

  /**
   * Converts a formatted string into a number, based on locale and number format.
   * @param value - The string to be unformatted.
   * @returns The raw number or undefined if parsing fails.
   */
  public unformat(value: string): number | undefined {
    if (value === '') return undefined;
    return this.getCleaner()(value);
  }

  private getFormatter() {
    switch (this._numberFormat) {
      case NumberFormat.Decimal:
        return this.formatDecimal.bind(this);
      case NumberFormat.Currency:
        return this.formatCurrency.bind(this);
      case NumberFormat.CurrencyValue:
        return this.formatCurrencyValue.bind(this);
      case NumberFormat.Percent:
        return this.formatPercent.bind(this);
      default:
        return this.formatDecimal.bind(this);
    }
  }

  /**
   * Formats a raw number as a decimal string.
   */
  private formatDecimal(raw: number): Result {
    const { minimumFractionDigits = 0, maximumFractionDigits = 20 } = this.props;

    const formatted = new Intl.NumberFormat(this._locale, {
      style: 'decimal',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(raw);

    return { raw, formatted: this._unit ? `${formatted} ${this._unit}` : formatted };
  }

  /**
   * Formats a raw number as a localized currency string (with symbol).
   */
  private formatCurrency(raw: number): Result {
    const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = this.props;

    const formatted = new Intl.NumberFormat(this._locale, {
      style: 'currency',
      currency: Country.getCurrencyByLocale(this._locale),
      currencyDisplay: 'symbol',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(raw);

    return { raw, formatted };
  }

  /**
   * Formats a raw number as a localized currency string using currency code (e.g., USD).
   */
  private formatCurrencyValue(raw: number): Result {
    const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = this.props;

    const formatted = new Intl.NumberFormat(this._locale, {
      style: 'currency',
      currency: Country.getCurrencyByLocale(this._locale),
      currencyDisplay: 'code',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(raw);

    return { raw, formatted };
  }

  /**
   * Formats a raw number as a percentage string.
   */
  private formatPercent(raw: number): Result {
    const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = this.props;

    const formatted = new Intl.NumberFormat(this._locale, {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(raw / 100);

    return { raw, formatted };
  }

  private getCleaner() {
    switch (this._numberFormat) {
      case NumberFormat.Decimal:
        return this.unformatNumber.bind(this);
      case NumberFormat.Currency:
        return this.unformatCurrency.bind(this);
      case NumberFormat.CurrencyValue:
        return this.unformatCurrencyValue.bind(this);
      case NumberFormat.Percent:
        return this.unformatPercent.bind(this);
      default:
        return this.unformatNumber.bind(this);
    }
  }

  /**
   * Detects locale-specific group and decimal separators.
   * @returns The group and decimal separators for the given locale.
   */
  private getSeparators() {
    const formatted = new Intl.NumberFormat(this._locale).format(1000.1);
    const match = formatted.match(/(\D+)/g);
    if (!match) return { group: ',', decimal: '.' };
    const [group, decimal] = match.length === 2 ? match : [',', '.'];
    return { group, decimal };
  }

  /**
   * Parses a locale-formatted decimal string into a number.
   */
  private unformatNumber(value: string) {
    const { group, decimal } = this.getSeparators();
    const normalized = value
      .replace(new RegExp(`\\${group}`, 'g'), '') // Remove group separator
      .replace(new RegExp(`\\${decimal}`), '.') // Replace decimal separator with dot
      .replace(/[^0-9.-]/g, '') // Remove any additional non-numeric characters except dot and minus
      .replace(/\s+/g, ''); // Remove any whitespace

    const number = Number(normalized);
    return isNaN(number) ? undefined : number;
  }

  /**
   * Removes the currency code from the string and parses the remaining numeric value.
   */
  private unformatCurrencyValue(value: string) {
    const currencyCode = Country.getCurrencyByLocale(this._locale) as string;
    const cleaned = value.replace(new RegExp(currencyCode, 'gi'), '').trim();
    return this.unformatNumber(cleaned);
  }

  /**
   * Removes the currency symbol and parses the numeric value.
   */
  private unformatCurrency(value: string) {
    const currencySymbol = Country.getCurrencySymbolByLocale(this._locale) as string;
    const escapedSymbol = this.escapeRegExp(currencySymbol);
    const cleaned = value
      .replace(new RegExp(`[\\s]*${escapedSymbol}[\\s]*`, 'gi'), '')
      .trim();
    return this.unformatNumber(cleaned);
  }

  /**
   * Removes the percent symbol and parses the numeric value.
   */
  private unformatPercent(value: string) {
    return this.unformatNumber(value.replace(/%/g, ''));
  }

  /**
   * Escapes RegExp special characters in a string.
   */
  private escapeRegExp(str: string) {
    return str.replace(/[$()*+.[\]{}\\^?|]/g, '\\$&');
  }
}
